// src/lib/admin/adminPartners.js
import { supabase } from "../../supabaseClient";

function mapPartner(row) {
  return {
    ...row,

    team_member_count: Number(
      row.team_member_count || 0
    ),

    active_team_member_count: Number(
      row.active_team_member_count || 0
    ),

    owner_count: Number(
      row.owner_count || 0
    ),

    admin_count: Number(
      row.admin_count || 0
    ),

    cohort_count: Number(
      row.cohort_count || 0
    ),

    active_cohort_count: Number(
      row.active_cohort_count || 0
    ),

    learner_count: Number(
      row.learner_count || 0
    ),

    active_learner_count: Number(
      row.active_learner_count || 0
    ),

    pending_team_invitation_count:
      Number(
        row.pending_team_invitation_count ||
          0
      ),

    pending_learner_invitation_count:
      Number(
        row.pending_learner_invitation_count ||
          0
      ),
  };
}

function mapNullableNumber(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : null;
}

function mapProgrammeAccess(row) {
  const data = row || {};

  return {
    ...data,

    configured:
      data.configured === true,

    max_cohorts:
      mapNullableNumber(
        data.max_cohorts
      ),

    cohort_usage: Number(
      data.cohort_usage || 0
    ),

    cohorts_remaining:
      mapNullableNumber(
        data.cohorts_remaining
      ),

    max_learners:
      mapNullableNumber(
        data.max_learners
      ),

    learner_usage: Number(
      data.learner_usage || 0
    ),

    learners_remaining:
      mapNullableNumber(
        data.learners_remaining
      ),

    sponsored_access_type:
      data.sponsored_access_type ||
      "none",

    effective_status:
      data.effective_status ||
      "not_configured",
  };
}

function normaliseLimit(
  value,
  label
) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const number = Number(value);

  if (
    !Number.isInteger(number) ||
    number < 0
  ) {
    throw new Error(
      `${label} must be a whole number of 0 or more, or left blank for unlimited.`
    );
  }

  return number;
}

export async function fetchAdminPartnerOrganisations(
  partnerId = null
) {
  const { data, error } =
    await supabase.rpc(
      "admin_get_partner_organisations",
      {
        p_partner_id:
          partnerId || null,
      }
    );

  if (error) {
    throw error;
  }

  return Array.isArray(data)
    ? data.map(mapPartner)
    : [];
}

export async function createAdminPartnerOrganisation({
  name,
}) {
  const cleanedName = String(
    name || ""
  ).trim();

  if (cleanedName.length < 2) {
    throw new Error(
      "Please enter a partner organisation name."
    );
  }

  const { data, error } =
    await supabase.rpc(
      "admin_create_partner_organisation",
      {
        p_name: cleanedName,
      }
    );

  if (error) {
    throw error;
  }

  if (!data?.success) {
    throw new Error(
      data?.error ||
        "The partner organisation could not be created."
    );
  }

  return data;
}

export async function updateAdminPartnerOrganisation({
  partnerId,
  name,
  status,
}) {
  if (!partnerId) {
    throw new Error(
      "The partner organisation could not be identified."
    );
  }

  const cleanedName =
    name === undefined ||
    name === null
      ? null
      : String(name).trim();

  const cleanedStatus =
    status === undefined ||
    status === null
      ? null
      : String(status)
          .trim()
          .toLowerCase();

  const { data, error } =
    await supabase.rpc(
      "admin_update_partner_organisation",
      {
        p_partner_id: partnerId,
        p_name: cleanedName,
        p_status: cleanedStatus,
      }
    );

  if (error) {
    throw error;
  }

  if (!data?.success) {
    throw new Error(
      data?.error ||
        "The partner organisation could not be updated."
    );
  }

  return data;
}

export async function fetchAdminPartnerProgrammeAccess(
  partnerId
) {
  if (!partnerId) {
    throw new Error(
      "The partner organisation could not be identified."
    );
  }

  const { data, error } =
    await supabase.rpc(
      "admin_get_partner_programme_access",
      {
        p_partner_id: partnerId,
      }
    );

  if (error) {
    throw error;
  }

  return mapProgrammeAccess(data);
}

export async function setAdminPartnerProgrammeAccess({
  partnerId,
  status,
  startsAt,
  endsAt,
  maxCohorts,
  maxLearners,
  sponsoredAccessType,
}) {
  if (!partnerId) {
    throw new Error(
      "The partner organisation could not be identified."
    );
  }

  const cleanedStatus = String(
    status || ""
  )
    .trim()
    .toLowerCase();

  if (
    ![
      "draft",
      "active",
      "suspended",
    ].includes(cleanedStatus)
  ) {
    throw new Error(
      "Programme status must be draft, active or suspended."
    );
  }

  const cleanedAccessType = String(
    sponsoredAccessType || "none"
  )
    .trim()
    .toLowerCase();

  if (
    ![
      "none",
      "full_journey",
    ].includes(cleanedAccessType)
  ) {
    throw new Error(
      "Sponsored learning access must be none or full journey."
    );
  }

  const cleanedMaxCohorts =
    normaliseLimit(
      maxCohorts,
      "Maximum cohorts"
    );

  const cleanedMaxLearners =
    normaliseLimit(
      maxLearners,
      "Maximum learners"
    );

  const { data, error } =
    await supabase.rpc(
      "admin_set_partner_programme_access",
      {
        p_partner_id: partnerId,
        p_status: cleanedStatus,
        p_starts_at:
          startsAt || null,
        p_ends_at:
          endsAt || null,
        p_max_cohorts:
          cleanedMaxCohorts,
        p_max_learners:
          cleanedMaxLearners,
        p_sponsored_access_type:
          cleanedAccessType,
      }
    );

  if (error) {
    throw error;
  }

  return mapProgrammeAccess(data);
}